import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';import { useAuth } from '../App';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Menu, Search, MessageSquare, LogOut, User, Bell, LayoutGrid, Mail, HelpCircle, ChevronDown, Home, Check, ArrowLeftRight, Store, UserRound } from 'lucide-react';
import OnboardingStrip from './OnboardingStrip';
import FeedbackDialog from './FeedbackDialog';
import NotificationCenter from './NotificationCenter';
import { getVendorAvatar } from '../utils/vendorImage';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Navigation component
 * @param {Object} props
 * @param {boolean} props.showFeedback - Show feedback button
 * @param {boolean} props.hideMobileNav - Hide mobile hamburger menu (when using bottom tab bar)
 */
const Navigation = ({ showFeedback = false, hideMobileNav = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, activeRole, canSwitchRoles, setActiveRole, isRoleSwitchBlocked } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationType, setNotificationType] = useState('inquiries'); // 'inquiries' or 'messages'
  const [presentationMode, setPresentationMode] = useState(false);

  // ─── App-shell scroll condense (additive, global) ─────────────────────────
  // Observes window scroll and flips a single `scrolled` flag when the user
  // is more than SCROLL_THRESHOLD past the top. Threshold is small (16px) so
  // the condense triggers early; state is throttled via rAF to avoid layout
  // thrash. No side effects outside the two <header>s below.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const SCROLL_THRESHOLD = 16;
    let ticking = false;
    const update = () => {
      ticking = false;
      const y = window.scrollY || window.pageYOffset || 0;
      setScrolled((prev) => {
        const next = y > SCROLL_THRESHOLD;
        return prev === next ? prev : next;
      });
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };
    update(); // initial sync
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ─── Subtle role tint applied to both mobile & desktop headers ────────────
  // Deliberately low-saturation (≤ 50/50 alpha) so it reads as ambient context
  // rather than a banner. Admin detection prefers user.is_admin as authoritative.
  const roleTintClasses = (() => {
    if (!user) return 'bg-white';
    const effective = user.is_admin ? 'admin' : (activeRole || 'customer');
    if (effective === 'vendor') return 'bg-gradient-to-b from-emerald-50/60 via-white to-white';
    if (effective === 'admin') return 'bg-gradient-to-b from-amber-50/60 via-white to-white';
    return 'bg-gradient-to-b from-slate-50/60 via-white to-white';
  })();

  // ─── Header avatar photo (mobile header cluster) ───────────────────────────
  // Resolves the image source for the small circular avatar next to the bell
  // on mobile, using the existing canonical chain — never creates new image
  // logic, never mutates stored photos. Fetched once per role change.
  //
  //   provider mode → vendor.profile_photo → vendor.owner_photo → initial
  //   customer mode → customer_profile.profile_photo             → initial
  //
  // The result is a plain URL string or null. If null, the avatar falls back
  // to rendering the user's name initial in the same ring treatment the
  // desktop avatar already uses.
  //
  // Cross-route blink fix (Feb 2026): every page in the app mounts its own
  // `<Navigation>` instance, so navigating Home ↔ Browse unmounts + remounts
  // Navigation, resetting `headerPhoto` to null and causing a visible
  // initial-letter flash during the brief re-fetch window. We cache the
  // resolved photo URL in `sessionStorage` keyed by (user id, effective
  // role) so the useState lazy initializer can restore it synchronously on
  // remount — the letter never appears after the first successful load.
  // Logout clears sessionStorage; role switches use a distinct cache key;
  // the fetch still runs on mount to keep the cached value fresh.
  const headerPhotoCacheKey = () => {
    const uid = user?.id || 'anon';
    const effective = activeRole || user?.role || 'customer';
    return `celey:headerPhoto:${uid}:${effective}`;
  };
  const [headerPhoto, setHeaderPhoto] = useState(() => {
    try {
      if (typeof window === 'undefined') return null;
      const uid = user?.id || 'anon';
      const effective = activeRole || user?.role || 'customer';
      return sessionStorage.getItem(`celey:headerPhoto:${uid}:${effective}`) || null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    let cancelled = false;
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!user || !token) {
      setHeaderPhoto(null);
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    const effective = activeRole || user.role;
    // Synchronously pick up any cached photo for this (user, role) combo
    // when dependencies change (e.g. role switch) so the UI never flashes
    // the letter if a prior fetch already succeeded this session.
    try {
      const cached = sessionStorage.getItem(headerPhotoCacheKey());
      if (cached) setHeaderPhoto(cached);
    } catch {}
    const writeCache = (src) => {
      try {
        const k = headerPhotoCacheKey();
        if (src) sessionStorage.setItem(k, src);
        else sessionStorage.removeItem(k);
      } catch {}
    };
    // Shared fetcher used by mount AND the `celey:profile-photo-updated`
    // refresh signal. Returns void; updates `headerPhoto` + cache.
    const fetchHeaderPhoto = async () => {
      try {
        if (effective === 'vendor') {
          const res = await axios.get(`${API}/api/vendors/me/profile`, { headers });
          if (!cancelled) {
            const src = res?.data ? getVendorAvatar(res.data) : null;
            setHeaderPhoto(src || null);
            writeCache(src || null);
          }
        } else {
          const res = await axios.get(`${API}/api/customers/me/profile`, { headers });
          if (!cancelled) {
            const src = (res?.data?.profile_photo || '').trim();
            setHeaderPhoto(src || null);
            writeCache(src || null);
          }
        }
      } catch {
        if (!cancelled) setHeaderPhoto(null);
      }
    };
    fetchHeaderPhoto();
    // Listen for in-app profile photo updates (dispatched by the customer
    // and vendor dashboards after a successful upload). The listener
    // invalidates the sessionStorage cache for the current (user, role)
    // and re-runs the fetch so the header reflects the new photo without
    // requiring a route change or app restart.
    const onPhotoUpdated = () => {
      try { sessionStorage.removeItem(headerPhotoCacheKey()); } catch {}
      // Clear stale error state so a previously-broken image doesn't keep
      // the fallback initial pinned after the user uploads a fresh photo.
      setHeaderImgError(false);
      fetchHeaderPhoto();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('celey:profile-photo-updated', onPhotoUpdated);
    }
    return () => {
      cancelled = true;
      if (typeof window !== 'undefined') {
        window.removeEventListener('celey:profile-photo-updated', onPhotoUpdated);
      }
    };
  }, [user?.id, activeRole, user?.role]);

  // Role-appropriate profile/settings route for avatar tap.
  const goToProfile = () => {
    const effective = activeRole || user?.role;
    if (user?.is_admin) {
      navigate('/admin/dashboard?tab=profile');
    } else if (effective === 'vendor') {
      navigate('/vendor/dashboard?tab=profile');
    } else {
      navigate('/customer-dashboard?tab=profile');
    }
  };

  // Header-avatar image-error state (lifted from the previously nested
  // HeaderAvatar component so the <img> DOM node stays stable across
  // Navigation re-renders — prior nested-declaration caused an unmount/remount
  // on every route change, producing a visible blink).
  const [headerImgError, setHeaderImgError] = useState(false);
  useEffect(() => { setHeaderImgError(false); }, [headerPhoto, activeRole, user?.role]);

  const closeMenu = () => setIsOpen(false);

  // Role-aware route configurations
  const CUSTOMER_ROUTES = [
    '/customer-dashboard',
    '/browse',
    '/vendors/', // Vendor detail pages are part of customer browse flow
    '/plan-event', // Event planning flow
    '/events/', // Event detail pages
  ];
  
  const VENDOR_ROUTES = [
    '/vendor/dashboard',
    '/vendor/',
  ];

  // Get role-aware home navigation
  const handleLogoClick = () => {
    // If not logged in, go to landing page
    if (!user) {
      navigate('/');
      return;
    }

    // Determine current role (prefer activeRole, fallback to user.role)
    const currentRole = activeRole || user.role;

    if (currentRole === 'vendor') {
      // Vendor mode: Navigate to vendor route
      const lastVendorRoute = localStorage.getItem('celey:lastRoute:vendor');
      
      // Validate the stored route is a valid vendor route
      if (lastVendorRoute && VENDOR_ROUTES.some(r => lastVendorRoute.startsWith(r))) {
        navigate(lastVendorRoute, { replace: true });
      } else {
        // Fallback to vendor dashboard
        navigate('/vendor/dashboard', { replace: true });
      }
    } else if (currentRole === 'customer') {
      // Customer mode: Always navigate to Home (customer dashboard)
      navigate('/customer-dashboard', { replace: true });
    } else if (user.is_admin) {
      // Admin mode
      navigate('/admin/dashboard', { replace: true });
    } else {
      // Unknown role - go to landing
      navigate('/', { replace: true });
    }
  };

  // Fetch notification count - defined with useCallback before being used
  const fetchNotificationCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      
      if (user?.role === 'customer') {
        // For customers: count inquiries with unread messages
        const response = await axios.get(`${API}/api/inquiries/customer`, { headers });
        // Count inquiries with unread messages
        const unreadCount = response.data.filter(inq => inq.unread_count > 0).length;
        setNotificationCount(unreadCount);
        // Priority: Messages (proposals) first, then inquiries
        if (unreadCount > 0) {
          setNotificationType('messages');
        } else {
          setNotificationType('inquiries');
        }
      } else if (user?.role === 'vendor') {
        // For vendors: count inquiries with unread messages
        const response = await axios.get(`${API}/api/inquiries/vendor`, { headers });
        // Count inquiries with unread messages
        const unreadCount = response.data.filter(inq => inq.unread_count > 0).length;
        setNotificationCount(unreadCount);
        
        // Priority: Messages (proposals) first, then new inquiries
        if (unreadCount > 0) {
          setNotificationType('messages');
        } else {
          setNotificationType('inquiries');
        }
      }
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  }, [user?.role]);

  // Fetch notification count
  useEffect(() => {
    if (user) {
      fetchNotificationCount();
      // Refresh every 30 seconds
      const intervalId = setInterval(fetchNotificationCount, 30000);
      return () => clearInterval(intervalId);
    }
  }, [user, fetchNotificationCount]);

  // Check presentation mode for admin banner
  useEffect(() => {
    if (user?.is_admin) {
      axios.get(`${API}/api/presentation-mode`).then(r => setPresentationMode(r.data.presentation_mode)).catch(() => {});
      const iv = setInterval(() => {
        axios.get(`${API}/api/presentation-mode`).then(r => setPresentationMode(r.data.presentation_mode)).catch(() => {});
      }, 15000);
      return () => clearInterval(iv);
    }
  }, [user]);

  // Role switch handler (reuses existing auth context)
  const isDualRole = canSwitchRoles && canSwitchRoles();
  const switchBlocked = isRoleSwitchBlocked && isRoleSwitchBlocked(location.pathname);
  const handleRoleSwitch = (role) => {
    if (switchBlocked) {
      toast.error('Complete the current flow before switching roles');
      return;
    }
    if (role === activeRole) return;
    // Silent: the RolePill color morph + URL change are already clear
    // feedback; a toast on top of that is the noisy behavior the new
    // tap-to-toggle UX explicitly replaces.
    setActiveRole(role, { silent: true });
    const destination = role === 'vendor' ? '/vendor/dashboard' : '/customer-dashboard';
    navigate(destination, { replace: true });
  };

  // Binary tap-to-toggle role pill. Shows CURRENT mode; a tap flips to the
  // other mode with a smooth color morph + text crossfade. No dropdown.
  const RolePill = ({ size = 'md', testId }) => {
    const isVendor = activeRole === 'vendor';
    const other = isVendor ? 'customer' : 'vendor';
    // Track a short "morph" flag so the text crossfade and micro-bounce
    // play once per tap without re-triggering on every render.
    const [morphing, setMorphing] = useState(false);
    useEffect(() => {
      setMorphing(true);
      const t = setTimeout(() => setMorphing(false), 260);
      return () => clearTimeout(t);
    }, [isVendor]);

    const sizeClasses =
      size === 'sm'
        ? 'h-7 px-2.5 gap-1 text-[11px]'
        : 'h-8 px-3 gap-1.5 text-xs';
    const tint = isVendor
      ? 'bg-emerald-50 border-emerald-200 text-emerald-700 shadow-[0_0_0_3px_rgba(16,185,129,0.08)]'
      : 'bg-slate-50 border-slate-200 text-slate-700 shadow-[0_0_0_3px_rgba(100,116,139,0.06)]';
    const iconTint = isVendor ? 'text-emerald-500' : 'text-slate-400';

    return (
      <button
        type="button"
        onClick={() => handleRoleSwitch(other)}
        disabled={switchBlocked}
        aria-label={`Switch to ${isVendor ? 'Customer' : 'Provider'} mode`}
        title={`Tap to switch to ${isVendor ? 'Customer' : 'Provider'} mode`}
        data-testid={testId}
        data-role-pill-mode={activeRole}
        className={`inline-flex items-center font-medium rounded-full border transition-all duration-300 ease-out active:scale-[0.96] will-change-transform ${sizeClasses} ${tint} ${switchBlocked ? 'opacity-50 cursor-not-allowed' : 'hover:brightness-[0.98]'}`}
      >
        <ArrowLeftRight className={`h-3 w-3 ${iconTint} transition-colors duration-300`} aria-hidden="true" />
        <span
          key={activeRole /* re-mounts on change → css transition triggers */}
          className={`inline-block transition-opacity duration-200 ${morphing ? 'opacity-90' : 'opacity-100'}`}
        >
          {isVendor ? 'Provider' : 'Customer'}
        </span>
      </button>
    );
  };

  const NavLinks = ({ mobile = false }) => {
    const buttonClass = mobile ? "w-full justify-start" : "";
    const spacing = mobile ? "flex flex-col space-y-2" : "flex items-center space-x-2 md:space-x-4";

    return (
      <div className={spacing}>
        {/* Desktop: For logged-in customers, show Home and Browse */}
        {!mobile && user && (activeRole || user.role) === 'customer' && (
          <>
            <Button 
              variant="ghost"
              onClick={() => navigate('/customer-dashboard')}
              className={buttonClass}
              data-testid="nav-home-btn"
            >
              <Home className="h-4 w-4 mr-2" /> Home
            </Button>
            <Button 
              variant="ghost"
              onClick={() => navigate('/browse')}
              className={buttonClass}
              data-testid="nav-browse-btn"
            >
              <Search className="h-4 w-4 mr-2" /> Browse
            </Button>
          </>
        )}

        {/* Desktop: For logged-in vendors, show Dashboard */}
        {!mobile && user && (activeRole || user.role) === 'vendor' && (
          <Button 
            variant="ghost"
            onClick={() => navigate('/vendor/dashboard')}
            className={buttonClass}
            data-testid="nav-vendor-dashboard-btn"
          >
            <Home className="h-4 w-4 mr-2" /> Dashboard
          </Button>
        )}

        {/* Desktop: For non-logged-in users, show FAQs and Contact */}
        {!mobile && !user && (
          <>
            <Button 
              variant="ghost" 
              onClick={() => {
                navigate('/faqs');
              }}
              className={buttonClass}
            >
              <HelpCircle className="h-4 w-4 mr-2" /> FAQs
            </Button>

            <Button 
              variant="ghost" 
              onClick={() => {
                navigate('/contact');
              }}
              className={buttonClass}
            >
              <Mail className="h-4 w-4 mr-2" /> Contact
            </Button>
          </>
        )}

        {user ? (
          <>
            {/* Mobile Menu Order: Dashboard (with notification badge), Browse, Feedback, FAQs, Contact, Logout */}
            
            {/* Dashboard Button - Mobile First (Primary destination with notification indicator) */}
            {mobile && (
              <Button 
                variant="ghost"
                onClick={() => {
                  const dashboardPath = user.role === 'vendor' 
                    ? '/vendor/dashboard' 
                    : user.is_admin 
                      ? '/admin/dashboard' 
                      : '/customer-dashboard';
                  navigate(dashboardPath);
                  closeMenu();
                }}
                className={`${buttonClass} relative`}
              >
                {(activeRole || user.role) === 'customer' ? (
                  <><Home className="h-4 w-4 mr-2" /> Home</>
                ) : (
                  <><LayoutGrid className="h-4 w-4 mr-2" /> Dashboard</>
                )}
                {notificationCount > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </span>
                )}
              </Button>
            )}

            {/* Browse Service Providers - Mobile Second */}
            {mobile && (
              <Button 
                variant="ghost"
                onClick={() => {
                  navigate('/browse');
                  closeMenu();
                }}
                className={buttonClass}
              >
                <Search className="h-4 w-4 mr-2" /> Browse Service Providers
              </Button>
            )}

            {/* Feedback - Mobile Third (only if showFeedback is true) */}
            {mobile && showFeedback && (
              <Button 
                variant="ghost" 
                onClick={() => {
                  closeMenu();
                  navigate('/feedback');
                }}
                className={buttonClass}
              >
                <MessageSquare className="h-4 w-4 mr-2" /> Feedback
              </Button>
            )}

            {/* FAQs - Mobile Fourth */}
            {mobile && (
              <Button 
                variant="ghost" 
                onClick={() => {
                  navigate('/faqs');
                  closeMenu();
                }}
                className={buttonClass}
              >
                <HelpCircle className="h-4 w-4 mr-2" /> FAQs
              </Button>
            )}

            {/* Contact - Mobile Fifth */}
            {mobile && (
              <Button 
                variant="ghost" 
                onClick={() => {
                  navigate('/contact');
                  closeMenu();
                }}
                className={buttonClass}
              >
                <Mail className="h-4 w-4 mr-2" /> Contact
              </Button>
            )}

            {/* Desktop: Role Switch Pill - binary tap toggle for dual-role users */}
            {!mobile && isDualRole && (
              <RolePill size="md" testId="role-switch-pill-desktop" />
            )}

            {/* Desktop: Notification Center */}
            {!mobile && (
              <NotificationCenter />
            )}

            {/* Desktop: Profile Dropdown Menu */}
            {!mobile && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    title={activeRole === 'vendor' ? 'Provider mode' : user.is_admin ? 'Admin mode' : 'Customer mode'}
                    data-role-mode={activeRole || user.role}
                  >
                    {/* Desktop header avatar — mirrors mobile avatar logic so
                        an uploaded profile photo replaces the initial when
                        available. Falls back to initial on null OR <img>
                        load error. Reuses existing `headerPhoto` /
                        `headerImgError` state. */}
                    {(headerPhoto && !headerImgError) ? (
                      <img
                        src={headerPhoto}
                        alt=""
                        className={`w-9 h-9 rounded-full object-cover shadow-md ring-2 ring-offset-2 ring-offset-white ${
                          user.is_admin
                            ? 'ring-amber-400/70'
                            : activeRole === 'vendor'
                              ? 'ring-emerald-500/70'
                              : 'ring-slate-300/70'
                        }`}
                        onError={() => setHeaderImgError(true)}
                        data-testid="user-avatar"
                      />
                    ) : (
                      <div
                        className={`flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 text-white font-semibold text-sm shadow-md transition-shadow ring-2 ring-offset-2 ring-offset-white ${
                          user.is_admin
                            ? 'ring-amber-400/70'
                            : activeRole === 'vendor'
                              ? 'ring-emerald-500/70'
                              : 'ring-slate-300/70'
                        }`}
                        data-testid="user-avatar"
                      >
                        {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                      </div>
                    )}
                    <div className="hidden lg:block text-left">
                      <div className="text-sm font-medium text-gray-900">
                        {user.name ? user.name.split(' ')[0] : 'User'}
                      </div>
                    </div>
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user.name || 'User'}</p>
                      <p className="text-xs text-gray-500 capitalize">{user.role} Account</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      const dashboardPath = user.is_admin ? '/admin/dashboard?tab=profile' : 
                                           user.role === 'vendor' ? '/vendor/dashboard?tab=profile' : 
                                           '/customer-dashboard?tab=profile';
                      navigate(dashboardPath);
                    }}
                  >
                    <User className="h-4 w-4 mr-2" />
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/faqs')}>
                    <HelpCircle className="h-4 w-4 mr-2" />
                    FAQs
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contact')}>
                    <Mail className="h-4 w-4 mr-2" />
                    Contact
                  </DropdownMenuItem>
                  {showFeedback && (
                    <DropdownMenuItem onClick={() => navigate('/feedback')}>
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Feedback
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => {
                      // Feb 2026: logout() owns the route transition.
                      logout();
                    }}
                    className="text-red-600 focus:text-red-600"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Logout - Mobile menu only (Desktop has it in dropdown) */}
            {mobile && (
              <Button 
                variant="ghost"
                onClick={() => {
                  // Feb 2026: logout() owns the route transition; keep
                  // closeMenu() so the mobile drawer collapses immediately.
                  logout();
                  closeMenu();
                }}
                className={buttonClass}
              >
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </Button>
            )}
          </>
        ) : (
          <>
            {/* Browse Service Providers */}
            <Button 
              variant={mobile ? "ghost" : "outline"}
              onClick={() => {
                navigate('/browse');
                if (mobile) closeMenu();
              }}
              className={buttonClass}
            >
              <Search className="h-4 w-4 mr-2" /> Browse Service Providers
            </Button>

            {/* Sign In */}
            <Button 
              onClick={() => {
                navigate('/auth');
                if (mobile) closeMenu();
              }}
              className={buttonClass}
            >
              Sign In
            </Button>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Presentation Mode Admin Banner */}
      {presentationMode && user?.is_admin && (
        <div className="bg-amber-500 text-white text-center py-1.5 text-xs font-semibold tracking-wide z-50 relative" data-testid="presentation-mode-banner">
          PRESENTATION MODE ACTIVE — Only curated vendors visible
        </div>
      )}

      {/* Minimal mobile brand bar — shown on mobile when full nav is hidden (logged-in screens) */}
      {hideMobileNav && user && (
        <header
          className={`md:hidden sticky top-0 z-50 border-b transition-all duration-200 ease-out ${roleTintClasses} ${scrolled ? 'border-gray-200/80 shadow-[0_1px_0_rgba(15,23,42,0.04)]' : 'border-gray-200/60'}`}
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
          data-testid="mobile-brand-header"
          data-scrolled={scrolled ? 'true' : 'false'}
        >
          <div
            className={`flex items-center justify-between px-4 transition-[height] duration-200 ease-out ${scrolled ? 'h-10' : 'h-11'}`}
          >
            <div className="flex items-center cursor-pointer" onClick={handleLogoClick}>
              <img
                src="/celeyapp-icon-green.png"
                alt="Celey"
                className={`mr-1.5 transition-all duration-200 ${scrolled ? 'h-4 w-4' : 'h-5 w-5'}`}
              />
              <span className={`font-semibold text-gray-900 tracking-tight transition-all duration-200 ${scrolled ? 'text-[13px]' : 'text-[15px]'}`}>
                Celey
              </span>
            </div>
            <div className="flex items-center gap-2">
              <NotificationCenter />
              {/* ─────────────── Header Avatar (inlined — see audit: nested
                  declarations caused unmount/remount blink on each parent
                  re-render) ─────────────── */}
              {(() => {
                const effective = activeRole || user?.role;
                const isVendor = effective === 'vendor';
                const initial = user?.name ? user.name.charAt(0).toUpperCase() : 'U';
                const showImage = headerPhoto && !headerImgError;
                return (
                  <button
                    type="button"
                    onClick={goToProfile}
                    aria-label={`Open ${isVendor ? 'provider' : 'customer'} profile`}
                    title={isVendor ? 'Provider profile' : 'Customer profile'}
                    data-testid="header-avatar-mobile"
                    data-role-mode={effective}
                    className="inline-flex items-center justify-center h-11 w-11 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-1"
                  >
                    <span className={`relative inline-flex items-center justify-center h-9 w-9 rounded-full before:absolute before:inset-0 before:rounded-full before:pointer-events-none ${isVendor
                      ? 'ring-2 ring-emerald-100/60 shadow-[0_0_6px_rgba(16,185,129,0.25)] before:ring-1 before:ring-white/40'
                      : 'ring-2 ring-stone-300 before:ring-1 before:ring-white/50'
                    }`}>
                      {showImage ? (
                        <img
                          src={headerPhoto}
                          alt=""
                          className="h-9 w-9 rounded-full object-cover border border-gray-200"
                          onError={() => setHeaderImgError(true)}
                          data-testid="header-avatar-img"
                        />
                      ) : (
                        <span
                          className="flex items-center justify-center h-9 w-9 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 text-white text-[13px] font-semibold border border-white shadow-sm"
                          data-testid="header-avatar-initial"
                        >
                          {initial}
                        </span>
                      )}
                      {/* Mode badge overlay — vendor-only passive role cue. */}
                      {isVendor && (
                        <span
                          className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border flex items-center justify-center shadow-[0_1px_2px_rgba(15,23,42,0.12)] text-emerald-700 bg-white border-emerald-200"
                          aria-hidden="true"
                          data-testid="header-avatar-badge-vendor"
                        >
                          <Store className="h-2 w-2" strokeWidth={2.75} />
                        </span>
                      )}
                    </span>
                  </button>
                );
              })()}
              {/* ─────────────── Icon Role Toggle (inlined for same
                  stability reason as the avatar above) ─────────────── */}
              {isDualRole && (() => {
                const isVendor = activeRole === 'vendor';
                const other = isVendor ? 'customer' : 'vendor';
                const iconTint = isVendor ? 'text-emerald-600' : 'text-slate-500';
                return (
                  <button
                    type="button"
                    onClick={() => handleRoleSwitch(other)}
                    disabled={switchBlocked}
                    aria-label={`Switch to ${isVendor ? 'Customer' : 'Provider'} mode`}
                    title={`Switch to ${isVendor ? 'Customer' : 'Provider'} mode`}
                    data-testid="role-switch-icon-mobile"
                    data-role-pill-mode={activeRole}
                    className={`inline-flex items-center justify-center h-11 w-11 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-1 ${switchBlocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-flex items-center justify-center h-8 w-8 rounded-full bg-gray-100 border border-gray-200 transition-colors active:scale-[0.94] will-change-transform ${switchBlocked ? '' : 'hover:bg-gray-200/70'}`}
                    >
                      <ArrowLeftRight className={`h-3.5 w-3.5 ${iconTint}`} aria-hidden="true" />
                    </span>
                  </button>
                );
              })()}
            </div>
          </div>
        </header>
      )}

      {/* Full header — desktop always, mobile only when hideMobileNav is false */}
      <header
        className={`border-b sticky top-0 z-50 transition-all duration-200 ease-out ${roleTintClasses} ${hideMobileNav ? 'hidden md:block' : ''} ${scrolled ? 'shadow-[0_1px_0_rgba(15,23,42,0.04)]' : ''}`}
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        data-testid="full-header"
        data-scrolled={scrolled ? 'true' : 'false'}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex justify-between items-center transition-[height] duration-200 ease-out ${scrolled ? 'h-12' : 'h-16'}`}>
            {/* Logo - Role-aware navigation */}
            <div 
              className="flex items-center space-x-2 cursor-pointer flex-shrink-0" 
              onClick={handleLogoClick}
            >
              <img 
                src="/celeyapp-icon-green.png" 
                alt="Celey" 
                className={`transition-all duration-200 ${scrolled ? 'h-6 w-6 sm:h-7 sm:w-7' : 'h-7 w-7 sm:h-9 sm:w-9'}`}
              />
              <div className="flex flex-col">
                <span className={`font-bold text-gray-900 leading-tight transition-all duration-200 ${scrolled ? 'text-lg sm:text-xl' : 'text-xl sm:text-2xl'}`}>Celey</span>
                {/* Tagline fades out in condensed state — height still collapses via grid-row trick */}
                <span
                  className={`text-[10px] sm:text-xs text-gray-500 font-normal tracking-wide leading-tight overflow-hidden transition-all duration-200 ${scrolled ? 'opacity-0 max-h-0' : 'opacity-100 max-h-4'}`}
                  aria-hidden={scrolled}
                >
                  Plan events and book trusted local providers
                </span>
              </div>
            </div>

            {/* Sign In link - visible on mobile for non-logged-in users */}
            {!user && (
              <button
                onClick={() => navigate('/auth')}
                className="md:hidden text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
              >
                Sign In
              </button>
            )}

            {/* Desktop Navigation */}
            <div className="hidden md:flex">
              <NavLinks mobile={false} />
            </div>

            {/* Mobile Menu - Hidden when hideMobileNav is true (using bottom tab bar) */}
            {!hideMobileNav && (
              <div className="md:hidden">
                <Sheet open={isOpen} onOpenChange={setIsOpen}>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <Menu className="h-6 w-6" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[280px] sm:w-[350px]">
                    {/* Greeting Header */}
                    {user && (
                      <div className="pt-6 pb-4 border-b border-gray-200 mb-4">
                        <h2 className="text-xl font-bold text-gray-900 mb-1">
                          Welcome, {user.name ? user.name.split(' ')[0] : 'User'}!
                        </h2>
                        <p className="text-sm text-gray-500 uppercase tracking-wide">
                          {user.role === 'vendor' ? 'Service Provider' : user.role === 'customer' ? 'Customer Account' : 'Admin Account'}
                        </p>
                      </div>
                    )}
                    
                    <div className="flex flex-col space-y-4">
                    <NavLinks mobile={true} />
                  </div>
                </SheetContent>
              </Sheet>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Ambient onboarding strip — reads canonical phase from OnboardingContext.
          Renders only for vendor-mode users whose phase requires action. */}
      <OnboardingStrip />

      {/* Feedback Dialog - Works for both mobile and desktop */}
      {showFeedback && user && (
        <FeedbackDialog 
          isOpen={feedbackOpen} 
          onClose={() => setFeedbackOpen(false)}
          userRole={user.role}
        />
      )}
    </>
  );
};

export default Navigation;
